import { useEffect, useState } from "react";
import api from "@/lib/api";
import { formatCurrency } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DollarSign, Receipt } from "lucide-react";
import { toast } from "sonner";
import { SummaryCard } from "@/components/investments/SummaryCard";

export function TaxReportDialog({ open, onClose, currency }) {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear - 1); // default to prior year (typical tax filing window)
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancel = false;
    (async () => {
      setLoading(true); setData(null);
      try {
        const { data: r } = await api.get(`/investments/tax-report?year=${year}`);
        if (!cancel) setData(r);
      } catch (err) {
        toast.error(err.response?.data?.detail || "Couldn't load tax report");
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [open, year]);

  const exportCsv = () => {
    if (!data) return;
    const rows = [
      ["Symbol", "Name", "Sell date", "Acquired", "Days held",
       "Holding period", "Quantity", "Proceeds", "Fee",
       "Cost basis", "Realized"],
      ...data.sells.map((s) => [
        s.symbol || "", s.name || "", s.sell_date, s.acquisition_date || "",
        s.days_held ?? "", s.holding_period,
        s.quantity, s.proceeds, s.fee, s.cost_basis, s.realized,
      ]),
      [],
      ["Summary"],
      ["Total proceeds", data.summary.total_proceeds],
      ["Total fees", data.summary.total_fees],
      ["Total cost basis", data.summary.total_cost_basis],
      ["Short-term gain", data.summary.short_term_gain],
      ["Long-term gain", data.summary.long_term_gain],
      ["Net realized gain", data.summary.net_realized_gain],
      ["Dividend income", data.summary.dividend_income.total],
      ["Interest income", data.summary.interest_income.total],
    ];
    const csv = rows.map((r) => r.map((v) => {
      const s = String(v ?? "");
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    }).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `investments_tax_report_${year}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const yearOptions = Array.from({ length: 6 }).map((_, i) => currentYear - i);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-5xl" data-testid="tax-report-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Receipt className="w-5 h-5 text-[var(--brand)]" /> Year-end tax report
          </DialogTitle>
          <DialogDescription>
            1099-B style summary of every realized sell, plus dividend &amp; interest income totals.
            Cost basis is average-cost; reconcile with your broker's official 1099-B before filing.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-3">
          <Label className="text-[10px] uppercase tracking-widest text-[var(--muted-text)]">Tax year</Label>
          <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
            <SelectTrigger className="h-9 w-32" data-testid="tax-year"><SelectValue /></SelectTrigger>
            <SelectContent>
              {yearOptions.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="flex-1" />
          <Button variant="outline" onClick={exportCsv} disabled={!data || loading}
                  data-testid="tax-export-csv">
            <DollarSign className="w-4 h-4 mr-1" /> Export CSV
          </Button>
        </div>

        {loading || !data ? (
          <div className="text-center text-sm text-[var(--muted-text)] py-6">Loading…</div>
        ) : (
          <>
            {/* Summary cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <SummaryCard label="Net realized" value={formatCurrency(data.summary.net_realized_gain, currency)}
                           tone={data.summary.net_realized_gain >= 0 ? "up" : "down"}
                           testid="tax-net-realized" />
              <SummaryCard label="Short-term" value={formatCurrency(data.summary.short_term_gain, currency)}
                           tone={data.summary.short_term_gain >= 0 ? "up" : "down"}
                           testid="tax-st-gain" />
              <SummaryCard label="Long-term" value={formatCurrency(data.summary.long_term_gain, currency)}
                           tone={data.summary.long_term_gain >= 0 ? "up" : "down"}
                           testid="tax-lt-gain" />
              <SummaryCard label="Dividends + interest"
                           value={formatCurrency(
                             data.summary.dividend_income.total + data.summary.interest_income.total,
                             currency
                           )}
                           tone="neutral" testid="tax-income" />
            </div>

            {/* Sells table */}
            <div className="mt-3">
              <div className="text-[10px] uppercase tracking-widest text-[var(--muted-text)] mb-1">
                Realized sells ({data.sells.length})
              </div>
              {data.sells.length === 0 ? (
                <div className="card-flat p-6 text-center text-sm text-[var(--muted-text)]">
                  No realized sells in {year}. Any dividends or interest you received are in the top-right card.
                </div>
              ) : (
                <div className="card-flat overflow-hidden max-h-[380px] overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-[var(--card)]">
                      <tr className="text-left text-[10px] uppercase tracking-wider text-[var(--muted-text)] border-b border-[var(--divider)]">
                        <th className="px-3 py-2">Symbol</th>
                        <th className="px-3 py-2">Sold</th>
                        <th className="px-3 py-2">Held</th>
                        <th className="px-3 py-2 text-right">Qty</th>
                        <th className="px-3 py-2 text-right">Proceeds</th>
                        <th className="px-3 py-2 text-right">Cost basis</th>
                        <th className="px-3 py-2 text-right">Realized</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.sells.map((s) => (
                        <tr key={s.activity_id} className="border-b border-[var(--divider)] last:border-none"
                            data-testid={`tax-row-${s.activity_id}`}>
                          <td className="px-3 py-2 font-medium">{s.symbol || "—"}
                            <div className="text-[10px] text-[var(--muted-text)]">{s.name || ""}</div>
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">{s.sell_date}</td>
                          <td className="px-3 py-2">
                            <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                              s.holding_period === "long_term"
                                ? "bg-[#E8F5EC] text-[#2F6B4A]"
                                : "bg-[#FFF3E0] text-[#B45309]"
                            }`}>
                              {s.holding_period === "long_term" ? "Long-term" : "Short-term"}
                            </span>
                            <div className="text-[10px] text-[var(--muted-text)] mt-0.5">
                              {s.days_held != null ? `${s.days_held}d` : "—"}
                            </div>
                          </td>
                          <td className="px-3 py-2 text-right font-mono">{s.quantity}</td>
                          <td className="px-3 py-2 text-right font-mono">{formatCurrency(s.proceeds, currency)}</td>
                          <td className="px-3 py-2 text-right font-mono text-[var(--muted-text)]">{formatCurrency(s.cost_basis, currency)}</td>
                          <td className={`px-3 py-2 text-right font-mono ${s.realized >= 0 ? "text-[#2F6B4A]" : "text-[#B4283A]"}`}>
                            {formatCurrency(s.realized, currency)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="text-[10px] text-[var(--muted-text)] leading-relaxed pt-2 border-t border-[var(--divider)]">
              <b>Disclaimer:</b> {data.disclaimer}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
